// RecordInvoiceModal — record a new supplier invoice against a purchase order,
// or free-edit an existing one (a queried invoice edited + saved returns to
// review; the backend owns that status transition, the modal just PUTs).
//
// A two-step wizard. Step 1 ("select") shows the header fields (PO selector,
// invoice number/date/total, notes, SharePoint link, GST) plus a grouped
// multi-select catalogue of the contract's payables (ChargeCatalogue); Next
// validates the header only — zero ticks is a valid selection, since manual
// charges are added on step 2 — then reconciles the tick set into seeded
// charge drafts (reconcileSelection) and advances. Step 2 ("confirm") shows a
// summary line, the charge cards (payable-backed or manual), an "Add manual
// charge" action, and the balance strip + submit actions; Back re-derives
// the tick set from the current drafts (selectedPayableIds) so edited
// amounts and manually-added charges survive the round trip, and an
// unticked payable's card is dropped. Create mode opens on
// "select"; edit mode opens directly on "confirm" (there is nothing to tick —
// the source invoice's charges are already seeded).
//
// Local presentational state over the pure invoiceFormState leaf; the mutation
// hooks (create / update / approve) live here and their invalidate-on-success
// refetch owns the derived balances. The two-level charge → split editor drives
// live dual-axis feedback throughout: axis 1 is the after-this-invoice balance
// under each split (from the PO's live line remaining), axis 2 is the per-charge
// price match. A bottom balance strip reconciles the allocation against the
// invoice total.
//
// Every split edit routes through applySplitSync so a row's qty and amount
// stay consistent at the invoice-time rate (a qty edit recomputes the amount,
// an amount edit clears a typed qty back to the derived display); the field
// the sync just touched is tracked in `autoAdjusted` and passed down as
// `highlightSplit` so ChargeCard/SplitRow can ring it and print a caption —
// cleared on a 1.5s timer or reset by the seed guard on open/close. Two
// advisory-only notices ride along: crossChargeDuplicateLineIds surfaces an
// invoice-level notice above the balance strip when more than one charge
// draws from the same PO line, and each ChargeCard's own duplicateSplitLineIds
// notice offers a Combine action (onCombineLine -> combineSplitsForLine) to
// merge that one charge's same-line splits.
//
// A PO selector on step 1 drives which purchase order's lines the splits
// target; it defaults to the passed active PO. Changing it re-scopes the split
// lines (clearing any that no longer resolve to a line in the newly-selected
// PO). There is no per-invoice supplier field — the supplier is always the
// contract's own supplier.
//
// Validation is computed continuously for the submit gate but field errors are
// only DISPLAYED after an attempt has been made: header fields defer to
// `hasAttemptedNext` (a step-1 Next click), charge/split fields and the
// balance/price alerts defer to `hasSubmitted` (a save/approve attempt) —
// mirroring PurchaseOrderFormModal's validate-on-submit — so a freshly opened
// modal shows no red before the operator has done anything.
//
// Three gated submit actions, "Save as draft" on both steps, "Save to review
// queue" and "Approve & email finance" on step 2 only:
// - "Save as draft" only needs the header to be well-formed (any present
//   charge must still be valid) — it exists only where a draft can result
//   (creating, or editing an existing draft; see `showSaveDraft`), never on a
//   review/queried edit, since that would quietly hollow the invoice out via
//   the draft-leniency gate. A draft edit opens on the select step (there is
//   a catalogue to re-tick) rather than confirm.
// - "Save to review queue" validates on submit and needs structural validity;
//   saving an existing draft this way promotes it to review.
// - "Approve & email finance" opens `ApproveInvoiceDialog` once the full
//   advisory gate passes — structural validity AND balanced AND no overdrawn
//   split AND no over-price charge. The dialog's own confirm runs
//   save-then-approve and hands the `ApproveInvoiceResponse` to onApproved (a
//   later task opens the email-preview modal); the dialog renders a thrown
//   error inline and only closes itself on success.
//
// All three persist via `persist`/`handleSaveDraft`, which track the invoice
// this modal session has already created (`persisted: { id, version }`) so a
// retry after a failed approve (or a lost response) updates that invoice
// instead of minting a duplicate — `createInvoice` has no idempotency key.
// Every gate is advisory: the backend re-checks and a server-side 409
// surfaces inline without closing.

import { useState, useEffect } from "react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Alert from "../../common/Alert";
import CurrencyInput from "../../common/CurrencyInput";
import ChargeCard from "./ChargeCard";
import ChargeCatalogue from "./ChargeCatalogue";
import ApproveInvoiceDialog from "./ApproveInvoiceDialog";
import {
  useCreateInvoice,
  useUpdateInvoice,
  useApproveInvoice,
} from "../../../hooks/useFinance";
import { isConflictError } from "../../../services/api";
import { gstSuffix } from "../../../utils/gstSuffix";
import { formatAud } from "../../../utils/financeCalc";
import {
  makeEmptyInvoiceState,
  stateFromInvoice,
  addCharge,
  removeCharge,
  addSplit,
  removeSplit,
  patchCharge,
  patchSplit,
  selectedPayableIds,
  reconcileSelection,
  changePo,
  validateInvoiceForm,
  hasInvoiceErrors,
  isBalanced,
  allocatedTotal,
  hasOverdrawnSplit,
  hasOverPriceCharge,
  canSaveDraft,
  toCreateInput,
  toUpdateInput,
  parseMoney,
  contentionByLine,
  applySplitSync,
  crossChargeDuplicateLineIds,
  combineSplitsForLine,
  type InvoiceFormState,
} from "./invoiceFormState";
import { invoicedByPayable } from "./invoicedByPayable";
import type {
  Payables,
  PurchaseOrderWithDerived,
  InvoiceWithDerived,
  ApproveInvoiceResponse,
  ApproveInvoiceInput,
  Invoice,
} from "../../../types/finance";

export type InvoiceFormMode = "create" | "edit";

type WizardStep = "select" | "confirm";

interface RecordInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: InvoiceFormMode;
  contractId: string;
  /** The active PO the modal opened against — the default selection and the
   *  fallback when the selected `poId` doesn't resolve. */
  po: PurchaseOrderWithDerived;
  /** Every PO on the contract, offered in the PO selector so a recorded
   *  invoice can be re-pointed at a different PO. */
  purchaseOrders: PurchaseOrderWithDerived[];
  payables: Payables;
  /** Every invoice on the contract — used to surface per-line contention with
   *  other in-review invoices on this PO. */
  invoices: Invoice[];
  /** The invoice being edited (edit mode only). */
  source?: InvoiceWithDerived;
  /** Handed the approve response (invoice + email preview + warnings) so a later
   *  task can open the email-preview modal. */
  onApproved: (response: ApproveInvoiceResponse) => void;
}

const MODE_TITLE: Record<InvoiceFormMode, string> = {
  create: "Record invoice",
  edit: "Edit invoice",
};

export default function RecordInvoiceModal({
  isOpen,
  onClose,
  mode,
  contractId,
  po,
  purchaseOrders,
  payables,
  invoices,
  source,
  onApproved,
}: RecordInvoiceModalProps) {
  const createInvoice = useCreateInvoice();
  const updateInvoice = useUpdateInvoice();
  const approveInvoice = useApproveInvoice();

  const [state, setState] = useState<InvoiceFormState>(() =>
    makeEmptyInvoiceState(po.id),
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Defer field-error display until a submit has been attempted (validate-on-
  // submit): errors are always computed for the gate, but only rendered once
  // the operator has tried to save/approve.
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [step, setStep] = useState<WizardStep>("select");
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  // Defer step-1 header field-error display until a Next has been attempted —
  // mirrors hasSubmitted's role for step 2's charge/split/balance errors.
  const [hasAttemptedNext, setHasAttemptedNext] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);

  // The invoice this modal session has already persisted (id + latest
  // version). Survives a failed approve so the retry updates rather than
  // re-creates. Reset in the seed guard on open/close.
  const [persisted, setPersisted] = useState<{ id: string; version: number } | null>(
    null,
  );

  // Which split field was auto-adjusted by the qty<->amount sync — drives a
  // brief highlight so silent money changes are visible. Cleared on a timer
  // or by the next edit.
  const [autoAdjusted, setAutoAdjusted] = useState<{
    splitKey: string;
    field: "amount" | "qty";
  } | null>(null);
  useEffect(() => {
    if (!autoAdjusted) return;
    const timer = setTimeout(() => setAutoAdjusted(null), 1500);
    return () => clearTimeout(timer);
  }, [autoAdjusted]);

  // Render-time seed guard (Pattern 0): re-seed each time the modal opens or its
  // target changes, and reset on close so a reopen never retains prior input.
  const seedKey = isOpen ? `${mode}:${source?.id ?? "new"}:${po.id}` : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    const initial =
      mode === "edit" && source
        ? stateFromInvoice(source, payables)
        : makeEmptyInvoiceState(po.id);
    setState(initial);
    setSelected(selectedPayableIds(initial));
    // Edit mode has nothing to tick — the source invoice's charges are already
    // seeded — so it opens straight on the confirm step, EXCEPT a draft edit,
    // which needs the catalogue to add/remove ticked payables like a create.
    setStep(mode === "edit" && source?.status !== "draft" ? "confirm" : "select");
    setSubmitError(null);
    setHasSubmitted(false);
    setHasAttemptedNext(false);
    setApproveOpen(false);
    setPersisted(null);
    setAutoAdjusted(null);
  } else if (seedKey === null && seededFor !== null) {
    setSeededFor(null);
  }

  const isDraftSource = mode === "edit" && source?.status === "draft";
  // Save as draft exists only where a draft can result: creating a new
  // invoice, or editing an existing draft. A review/queried invoice must
  // not be quietly hollowed out via the draft-leniency gate — and once THIS
  // session has persisted the new invoice (a failed approve attempt saves
  // it to review first), a draft can no longer result either: the server
  // never demotes, so offering the button would report a draft save while
  // the invoice stayed in review.
  const showSaveDraft = (mode === "create" && persisted === null) || isDraftSource;
  // The one state where the button's absence needs explaining: the operator
  // could see it a moment ago, then a failed approve persisted the invoice.
  const draftUnavailable = mode === "create" && persisted !== null;

  // The PO the splits currently target — the selected one, falling back to the
  // opened-against PO when the selection can't be resolved. All line-scoped
  // logic (splits, line balances, contention, overdraw gate) keys off this.
  const activePo =
    purchaseOrders.find((p) => p.id === state.poId) ?? po;

  const errors = validateInvoiceForm(state);
  const structurallyValid = !hasInvoiceErrors(errors);
  const headerValid =
    !errors.fieldErrors.invoiceNumber &&
    !errors.fieldErrors.invoiceDate &&
    !errors.fieldErrors.totalAmount &&
    !errors.fieldErrors.sharePointUrl;
  const balanced = isBalanced(state);
  const overdrawn = hasOverdrawnSplit(state, activePo.lineBalances);
  const overPrice = hasOverPriceCharge(state);
  const approveEnabled = structurallyValid && balanced && !overdrawn && !overPrice;
  // Surfaced as the disabled button's title so the reason is discoverable at
  // the control, not just inferred from the balance strip above it.
  const approveDisabledReason = approveEnabled
    ? undefined
    : !structurallyValid
      ? "Fix the highlighted fields before approving."
      : "Resolve the balance and validation exceptions before approving.";

  const contention = contentionByLine(invoices, activePo.id, source?.id);
  // Per-payable draw totals for the catalogue's fully-invoiced greying —
  // excludes the invoice being edited so it never counts against itself.
  const invoicedTotals = invoicedByPayable(invoices, {
    excludeInvoiceId: source?.id,
  });

  const isSubmitting =
    createInvoice.isPending ||
    updateInvoice.isPending ||
    approveInvoice.isPending;

  const patchHeader = (patch: Partial<InvoiceFormState>) =>
    setState((prev) => ({ ...prev, ...patch }));

  const toggleSelected = (payableId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(payableId)) {
        next.delete(payableId);
      } else {
        next.add(payableId);
      }
      return next;
    });

  // Validates the header only — zero ticks is a valid selection, since manual
  // charges are added on step 2 — then reconciles the tick set into seeded
  // charge drafts and advances.
  const handleNext = () => {
    setHasAttemptedNext(true);
    if (!headerValid) return;
    setState((prev) =>
      reconcileSelection(prev, selected, payables, activePo.lines.map((l) => l.id)),
    );
    setStep("confirm");
  };

  // Re-derives the tick set from the current drafts (rather than reusing the
  // step-1 selection verbatim) so a charge added or removed on step 2 is
  // reflected back on the catalogue.
  const handleBack = () => {
    setSelected(selectedPayableIds(state));
    setStep("select");
  };

  const handleError = (err: unknown) => {
    if (isConflictError(err)) {
      setSubmitError("This invoice changed elsewhere — reload and try again.");
    } else {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save the invoice.",
      );
    }
  };

  // Persist the current form and return the resulting invoice id (create
  // yields a fresh id; edit reuses the source id). Re-uses the invoice this
  // session has already created/updated (`persisted`) rather than creating a
  // duplicate on retry. `statusOverride` carries the save-as-draft actions'
  // explicit "draft" request: sent as-is on the create/persisted branches,
  // but an edit of an existing draft needs no status at all (it stays a
  // draft implicitly). Called with no override, save-to-review/approve keep
  // their original behaviour: promote an edited draft source to review,
  // otherwise send no status.
  const persist = async (statusOverride?: "draft" | "review"): Promise<string> => {
    if (mode === "edit" && source) {
      const status =
        statusOverride === "draft" ? undefined : isDraftSource ? "review" : statusOverride;
      const updated = await updateInvoice.mutateAsync({
        contractId,
        invoiceId: source.id,
        input: toUpdateInput(
          state,
          persisted?.version ?? source.version,
          status ? { status } : undefined,
        ),
      });
      setPersisted({ id: source.id, version: updated.version });
      return source.id;
    }
    if (persisted) {
      const updated = await updateInvoice.mutateAsync({
        contractId,
        invoiceId: persisted.id,
        input: toUpdateInput(
          state,
          persisted.version,
          statusOverride ? { status: statusOverride } : undefined,
        ),
      });
      setPersisted({ id: persisted.id, version: updated.version });
      return persisted.id;
    }
    const created = await createInvoice.mutateAsync({
      contractId,
      input: toCreateInput(state, statusOverride ? { status: statusOverride } : undefined),
    });
    setPersisted({ id: created.id, version: created.version });
    return created.id;
  };

  const handleSave = async () => {
    setHasSubmitted(true);
    if (!structurallyValid) return;
    setSubmitError(null);
    try {
      await persist();
      onClose();
    } catch (err) {
      handleError(err);
    }
  };

  // Save-as-draft: header + any present charges must be well-formed; the
  // charge-count requirement is the review queue's, not a draft's. Shares
  // `persist`'s branching with "Save to review queue" — only the requested
  // status differs.
  const handleSaveDraft = async () => {
    setHasSubmitted(true);
    setHasAttemptedNext(true);
    if (!canSaveDraft(state)) return;
    setSubmitError(null);
    try {
      await persist("draft");
      onClose();
    } catch (err) {
      handleError(err);
    }
  };

  const handleApproveClick = () => {
    setHasSubmitted(true);
    if (!approveEnabled) return;
    setApproveOpen(true);
  };

  // The dialog shows thrown errors inline itself and only closes on success.
  // A persist/approve conflict is mapped to the same friendly copy `handleSave`
  // uses, rather than letting the raw error render in the dialog; any other
  // error just propagates back to the dialog as-is.
  const handleApproveConfirm = async (input: ApproveInvoiceInput) => {
    setSubmitError(null);
    try {
      const invoiceId = await persist();
      const response = await approveInvoice.mutateAsync({ contractId, invoiceId, input });
      onApproved(response);
      onClose();
    } catch (err) {
      throw isConflictError(err)
        ? new Error("This invoice changed elsewhere — reload and try again.")
        : err;
    }
  };

  const suffix = gstSuffix(state.gstApplies);
  const total = parseMoney(state.totalAmount) ?? 0;
  const allocated = allocatedTotal(state);
  const diff = Math.round((total - allocated) * 100) / 100;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={MODE_TITLE[mode]}
      maxWidth="sm:max-w-4xl"
    >
      <div className="space-y-4">
        {step === "select" ? (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="inv-po" className="label">
                  Purchase order <span className="text-red-600">*</span>
                </label>
                <select
                  id="inv-po"
                  className="input"
                  value={state.poId}
                  onChange={(e) =>
                    setState((prev) => {
                      const next = purchaseOrders.find((p) => p.id === e.target.value);
                      return changePo(
                        prev,
                        e.target.value,
                        next ? next.lines.map((l) => l.id) : [],
                      );
                    })
                  }
                  disabled={isSubmitting}
                >
                  {purchaseOrders.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.poNumber}
                      {p.description ? ` — ${p.description}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="inv-number" className="label">
                  Invoice number <span className="text-red-600">*</span>
                </label>
                <input
                  id="inv-number"
                  type="text"
                  className="input"
                  value={state.invoiceNumber}
                  onChange={(e) => patchHeader({ invoiceNumber: e.target.value })}
                  disabled={isSubmitting}
                  aria-invalid={hasAttemptedNext && !!errors.fieldErrors.invoiceNumber}
                />
                {hasAttemptedNext && errors.fieldErrors.invoiceNumber && (
                  <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                    {errors.fieldErrors.invoiceNumber}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="inv-date" className="label">
                  Invoice date <span className="text-red-600">*</span>
                </label>
                <input
                  id="inv-date"
                  type="date"
                  className="input"
                  value={state.invoiceDate}
                  onChange={(e) => patchHeader({ invoiceDate: e.target.value })}
                  disabled={isSubmitting}
                  aria-invalid={hasAttemptedNext && !!errors.fieldErrors.invoiceDate}
                />
                {hasAttemptedNext && errors.fieldErrors.invoiceDate && (
                  <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                    {errors.fieldErrors.invoiceDate}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="inv-total" className="label">
                  Invoice total ({suffix}) <span className="text-red-600">*</span>
                </label>
                <CurrencyInput
                  id="inv-total"
                  size="md"
                  value={state.totalAmount}
                  onChange={(raw) => patchHeader({ totalAmount: raw })}
                  ariaInvalid={hasAttemptedNext && !!errors.fieldErrors.totalAmount}
                  disabled={isSubmitting}
                />
                {hasAttemptedNext && errors.fieldErrors.totalAmount && (
                  <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                    {errors.fieldErrors.totalAmount}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="inv-notes" className="label">
                  Notes
                </label>
                <input
                  id="inv-notes"
                  type="text"
                  className="input"
                  value={state.notes}
                  onChange={(e) => patchHeader({ notes: e.target.value })}
                  disabled={isSubmitting}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="inv-sharepoint" className="label">
                  SharePoint link
                </label>
                <input
                  id="inv-sharepoint"
                  type="url"
                  className="input"
                  placeholder="https://…sharepoint.com/…"
                  value={state.sharePointUrl}
                  onChange={(e) => patchHeader({ sharePointUrl: e.target.value })}
                  disabled={isSubmitting}
                  aria-invalid={hasAttemptedNext && !!errors.fieldErrors.sharePointUrl}
                />
                {hasAttemptedNext && errors.fieldErrors.sharePointUrl && (
                  <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                    {errors.fieldErrors.sharePointUrl}
                  </p>
                )}
              </div>
              <div className="flex items-center sm:col-span-2">
                <label className="flex items-center gap-2 text-body text-slate-700 dark:text-slate-300">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded-sm border-slate-300 text-brand-600 focus:ring-brand-400 dark:border-slate-600 dark:bg-slate-900"
                    checked={state.gstApplies}
                    onChange={(e) => patchHeader({ gstApplies: e.target.checked })}
                    disabled={isSubmitting}
                  />
                  GST applies
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="text-h4 text-slate-900 dark:text-slate-100">
                Step 1 of 2 — Select charges
              </h4>
              <ChargeCatalogue
                payables={payables}
                selected={selected}
                onToggle={toggleSelected}
                disabled={isSubmitting}
                invoicedTotals={invoicedTotals}
              />
              <p className="text-caption text-slate-500 dark:text-slate-400">
                Ad-hoc items not listed here? Add them as manual charges against
                PO lines in step 2.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
              {draftUnavailable && (
                <span className="mr-auto text-caption text-slate-500 dark:text-slate-400">
                  Saved to the review queue — it can no longer become a draft.
                </span>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {showSaveDraft && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSaveDraft}
                  loading={createInvoice.isPending || updateInvoice.isPending}
                  disabled={isSubmitting}
                >
                  Save as draft
                </Button>
              )}
              <Button type="button" onClick={handleNext} disabled={isSubmitting}>
                {selected.size > 0
                  ? `Next — ${selected.size} charge${selected.size === 1 ? "" : "s"}`
                  : "Next"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p
              data-testid="invoice-step-summary"
              className="text-body-sm text-slate-600 dark:text-slate-400"
            >
              PO{" "}
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {activePo.poNumber}
              </span>{" "}
              · Invoice{" "}
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {state.invoiceNumber || "—"}
              </span>{" "}
              · {formatAud(total)} ({suffix})
            </p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-h4 text-slate-900 dark:text-slate-100">
                  Step 2 of 2 — Confirm prices &amp; lines
                </h4>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => setState((prev) => addCharge(prev))}
                  disabled={isSubmitting}
                >
                  Add manual charge
                </Button>
              </div>
              {hasSubmitted && errors.fieldErrors.charges && (
                <Alert variant="error">{errors.fieldErrors.charges}</Alert>
              )}
              {state.charges.map((charge, index) => (
                <ChargeCard
                  key={charge.key}
                  charge={charge}
                  index={index}
                  poLines={activePo.lines}
                  lineBalances={activePo.lineBalances}
                  contentionByLine={contention}
                  chargeError={hasSubmitted ? errors.chargeErrors[charge.key] : undefined}
                  splitErrors={hasSubmitted ? errors.splitErrors : {}}
                  highlightSplit={autoAdjusted}
                  onPatchCharge={(patch) =>
                    setState((prev) => patchCharge(prev, index, patch))
                  }
                  onPatchSplit={(splitIndex, patch) => {
                    const charge = state.charges[index];
                    const split = charge?.splits[splitIndex];
                    if (!charge || !split) return;
                    const sync = applySplitSync(charge, split, patch);
                    setAutoAdjusted(
                      sync.adjusted ? { splitKey: split.key, field: sync.adjusted } : null,
                    );
                    setState((prev) => patchSplit(prev, index, splitIndex, sync.patch));
                  }}
                  onAddSplit={() => setState((prev) => addSplit(prev, index))}
                  onRemoveSplit={(splitIndex) =>
                    setState((prev) => removeSplit(prev, index, splitIndex))
                  }
                  onRemoveCharge={() => setState((prev) => removeCharge(prev, index))}
                  canRemoveCharge={state.charges.length > 1}
                  onCombineLine={(poLineId) =>
                    setState((prev) => combineSplitsForLine(prev, index, poLineId))
                  }
                  disabled={isSubmitting}
                />
              ))}
            </div>

            {crossChargeDuplicateLineIds(state).length > 0 && (
              <p className="text-caption text-amber-700 dark:text-amber-400">
                More than one charge draws from the same PO line — the emailed
                table will show a row per allocation.
              </p>
            )}

            <div
              data-testid="invoice-balance-strip"
              className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-body-sm text-slate-600 dark:text-slate-400">
                  Allocated{" "}
                  <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
                    {formatAud(allocated)}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
                    {formatAud(total)}
                  </span>
                </span>
                {balanced ? (
                  <span className="text-caption font-medium text-emerald-700 dark:text-emerald-400">
                    Balanced
                  </span>
                ) : diff > 0 ? (
                  <span className="text-caption font-medium text-amber-700 dark:text-amber-400">
                    {formatAud(diff)} unallocated
                  </span>
                ) : (
                  <span className="text-caption font-medium text-red-600 dark:text-red-400">
                    Over-allocated {formatAud(-diff)}
                  </span>
                )}
              </div>
              {(overdrawn || overPrice) && (
                <ul className="mt-1 space-y-0.5 text-caption text-red-600 dark:text-red-400">
                  {overdrawn && <li>A split overdraws its PO line.</li>}
                  {overPrice && <li>A charge exceeds its agreed price.</li>}
                </ul>
              )}
            </div>

            {submitError && <Alert variant="error">{submitError}</Alert>}

            <div className="flex flex-wrap items-center justify-end gap-3 pt-1">
              {draftUnavailable && (
                <span className="mr-auto text-caption text-slate-500 dark:text-slate-400">
                  Saved to the review queue — it can no longer become a draft.
                </span>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {showSaveDraft && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleSaveDraft}
                  loading={createInvoice.isPending || updateInvoice.isPending}
                  disabled={isSubmitting}
                >
                  Save as draft
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={handleSave}
                loading={
                  (createInvoice.isPending || updateInvoice.isPending) &&
                  !approveInvoice.isPending
                }
                disabled={isSubmitting}
              >
                Save to review queue
              </Button>
              <Button
                type="button"
                onClick={handleApproveClick}
                loading={approveInvoice.isPending}
                disabled={!approveEnabled || isSubmitting}
                title={approveDisabledReason}
              >
                Approve &amp; email finance
              </Button>
            </div>
          </>
        )}
      </div>
      <ApproveInvoiceDialog
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        invoiceNumber={state.invoiceNumber}
        totalAmount={parseMoney(state.totalAmount) ?? 0}
        gstApplies={state.gstApplies}
        onConfirm={handleApproveConfirm}
        isSubmitting={isSubmitting}
      />
    </Modal>
  );
}
